import type { AddressFile } from '@provision/entity-types';
import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { isNullish } from 'remeda';
import { useCurrentUserProfile } from 'src/graphql';

import {
  useAddressFiles,
  useCreateAddressFile,
  useDeleteAddressFile,
  useUpdateAddressFile,
} from 'src/graphql/entities/address-file';
import { fileUrl, storagePath } from 'src/graphql/entities/address-file/utils';
import {
  useFileUpload,
  useMoveAttachmentToSiteFiles,
} from 'src/graphql/entities/files-upload';
import {
  CONVERSATION_ATTACHMENT_DRAG_DATA_TYPE,
  getConversationAttachmentDragData,
} from 'src/utils/conversation-attachment-drag';
import { downloadFileFromUrl } from 'src/utils/file';
import { useScrollPaginateHandler } from 'src/utils/hooks';
import { throttleAsync } from './utils';

const SCROLL_OFFSET_PX = 300;
const UPLOAD_CONCURRENCY = 3;

const hasConversationAttachmentDragType = (dataTransfer: DataTransfer): boolean =>
  Array.from(dataTransfer.types).includes(CONVERSATION_ATTACHMENT_DRAG_DATA_TYPE);

interface UseAddressFileListProps {
  addressId: string;
  partnerId: number | null | undefined;
  includeDeleted?: boolean;
}

export const useAddressFileList = ({ addressId, partnerId, includeDeleted }: UseAddressFileListProps) => {
  const listContainerRef = useRef<HTMLDivElement>(null);
  const conversationAttachmentDragDepthRef = useRef(0);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isConversationAttachmentDragActive, setIsConversationAttachmentDragActive] =
    useState(false);

  const resetConversationAttachmentDragState = useCallback(() => {
    conversationAttachmentDragDepthRef.current = 0;
    setIsConversationAttachmentDragActive(false);
  }, []);

  const {
    addressFiles,
    totalRecords: fileCount,
    isLoading: isLoadingAddressFiles,
    loadMore,
    hasMore,
    resetCache: resetAddressFilesCache,
  } = useAddressFiles({
    filters: addressId
      ? [{ attribute: 'addressId', operator: '=', value: addressId }]
      : undefined,
    includeDeleted,
  });

  const { deleteAddressFile } = useDeleteAddressFile();
  const { updateAddressFile } = useUpdateAddressFile();
  const { createAddressFile } = useCreateAddressFile();

  const { user } = useCurrentUserProfile();

  const { uploadFile } = useFileUpload();
  const { moveToSiteFiles, loading: isSavingConversationAttachment } =
    useMoveAttachmentToSiteFiles();

  useEffect(() => {
    if (!listContainerRef.current) return;

    const { scrollHeight, scrollTop, clientHeight } = listContainerRef.current;
    if (
      scrollHeight - scrollTop - clientHeight < SCROLL_OFFSET_PX &&
      !isLoadingAddressFiles &&
      hasMore
    ) {
      void loadMore();
    }
  }, [hasMore, isLoadingAddressFiles, loadMore]);

  const handleScroll = useScrollPaginateHandler({
    loadMore: () => {
      void loadMore();
      return null;
    },
    enabled: hasMore,
    thresholdPx: 100,
  });

  const handleFileUpload = useCallback(
    (files: File[]) => {
      if (!files?.length || isNullish(partnerId) || !addressId) {
        return;
      }

      setIsUploadingFiles(true);
      const fileTasks = files.map((file) => async () => {
        const getUrl = await uploadFile({
          file,
          fileKey: storagePath(partnerId, addressId),
          hasAttachments: true,
        });

        const key = getUrl?.split('/').pop() || '';
        if (!key) throw new Error('Failed to upload file');

        return createAddressFile({
          addressId,
          partnerId,
          userId: user?.id,
          file,
          key,
        });
      });

      void throttleAsync(fileTasks, UPLOAD_CONCURRENCY)
        .then(() => {
          resetAddressFilesCache({ refetch: true });
        })
        .finally(() => setIsUploadingFiles(false));
    },
    [createAddressFile, uploadFile, user?.id, addressId, partnerId, resetAddressFilesCache],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: (files) => {
      resetConversationAttachmentDragState();
      handleFileUpload?.(files);
    },
    onDropRejected: () => {
      resetConversationAttachmentDragState();
    },
  });

  const handleConversationAttachmentDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasConversationAttachmentDragType(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      conversationAttachmentDragDepthRef.current += 1;
      setIsConversationAttachmentDragActive(true);
    },
    [],
  );

  const handleConversationAttachmentDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (!hasConversationAttachmentDragType(event.dataTransfer)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      setIsConversationAttachmentDragActive(true);
    },
    [],
  );

  const handleConversationAttachmentDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (
        conversationAttachmentDragDepthRef.current === 0 &&
        !hasConversationAttachmentDragType(event.dataTransfer)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      conversationAttachmentDragDepthRef.current = Math.max(
        conversationAttachmentDragDepthRef.current - 1,
        0,
      );

      if (conversationAttachmentDragDepthRef.current === 0) {
        setIsConversationAttachmentDragActive(false);
      }
    },
    [],
  );

  const handleConversationAttachmentDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      const attachment = getConversationAttachmentDragData(event.dataTransfer);
      resetConversationAttachmentDragState();

      if (!attachment) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      void moveToSiteFiles({
        attachment,
        addressId,
        uploadedByUserId: user?.id,
      }).catch(() => undefined);
    },
    [
      addressId,
      moveToSiteFiles,
      resetConversationAttachmentDragState,
      user?.id,
    ],
  );

  const handleDeleteFile = useCallback(
    (file: AddressFile) => {
      if (!file.id) return;
      void deleteAddressFile(file);
    },
    [deleteAddressFile],
  );

  const handleFilePin = useCallback(
    (addressFile: AddressFile) => {
      void updateAddressFile(
        addressFile.id,
        { pinnedAt: new Date().toISOString() },
        addressFile,
      );
    },
    [updateAddressFile],
  );

  const handleRename = useCallback(
    (newName: string, addressFile: AddressFile) => {
      void updateAddressFile(addressFile.id, { fileName: newName }, addressFile);
    },
    [updateAddressFile],
  );

  const handleFileUnpin = useCallback(
    (addressFile: AddressFile) => {
      // @ts-expect-error Generate entity type does not accept null but its required here
      void updateAddressFile(addressFile.id, { pinnedAt: null }, addressFile);
    },
    [updateAddressFile],
  );

  const handleOpenFile = useCallback((addressFile: AddressFile) => {
    const getUrl = fileUrl(
      addressFile?.partnerId,
      addressFile?.addressId,
      addressFile?.fileKey,
    );

    if (getUrl) {
      window.open(getUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleDownloadFile = useCallback((addressFile: AddressFile) => {
    const getUrl = fileUrl(
      addressFile?.partnerId,
      addressFile?.addressId,
      addressFile?.fileKey,
    );

    if (getUrl) {
      void downloadFileFromUrl(getUrl, addressFile.fileName);
    }
  }, []);

  return {
    getRootProps,
    getInputProps,
    listContainerRef,
    handleScroll,
    addressFiles,
    fileCount,
    isLoadingAddressFiles,
    handleDeleteFile,
    handleFilePin,
    handleFileUnpin,
    handleDownloadFile,
    handleOpenFile,
    handleRename,
    handleFileUpload,
    isUploadingFiles,
    isSavingConversationAttachment,
    isConversationAttachmentDragActive,
    handleConversationAttachmentDragEnter,
    handleConversationAttachmentDragOver,
    handleConversationAttachmentDragLeave,
    handleConversationAttachmentDrop,
  };
};
